import { BreadcrumbBar } from 'features/breadcrumbBar'
import {
  OntologyContextType,
  useOntology,
  useTitle,
} from 'features/dynamicOntology'
import { useRolodexEntries } from 'features/rolodex'
import React, { useMemo } from 'react'
import { GeckoEntry } from 'types'

interface ObjectBreadcrumbsProps {
  entry: GeckoEntry
}

const getImmediateParentObjectId = (
  entry: GeckoEntry,
  ontology: OntologyContextType
): string | null => {
  const typeDef = ontology.ontology.types[entry.type]
  const availableParentTypes = typeDef?.requiredTags
  if (!availableParentTypes || availableParentTypes.length === 0) {
    return null
  }

  const immediateParentType = ontology.findLowestType(availableParentTypes)

  if (!immediateParentType) {
    return null
  }

  return entry.tags[immediateParentType as keyof typeof entry.tags] || null
}

export const ObjectBreadcrumbs: React.FC<ObjectBreadcrumbsProps> = ({
  entry,
}) => {
  const ontology = useOntology()

  const immediateParentId = useMemo(
    () => getImmediateParentObjectId(entry, ontology),
    [entry, ontology]
  )

  const { entries: parentEntries } = useRolodexEntries(
    immediateParentId ? { ids: [immediateParentId] } : undefined
  )

  // NB: We expect only one entry to be present in parentEntries
  const immediateParent = parentEntries.find(
    (parent) => parent.id === immediateParentId
  )

  const currentName = useTitle(entry)
  const parentName = useTitle(immediateParent)

  if (!immediateParentId || !immediateParent) {
    return null
  }

  // Include orgId in URL for legacy compatibility with sidebar org name
  // and to ensure clear ontology context when navigating between objects
  const orgId = entry.tags.organization || 'unknown'

  return (
    <BreadcrumbBar
      border={false}
      lastPage={parentName}
      currentPage={currentName}
      navigateTo={`/prism/${orgId}/${immediateParent.id}`}
      capitalizePageNames={false} // We're already formatting the names
    />
  )
}
